import { addDays, addSeconds, differenceInDays, differenceInMinutes, isSameDay, parse, startOfDay } from "date-fns";
import { min, sortBy } from "lodash-es";
import { Fragment, useEffect, useMemo, useRef, useState } from "react";
import { twJoin } from "tailwind-merge";

import type { AssetBookingDto } from "@/api/types";
import { IconButton } from "@/components/Button/IconButton";
import { DateRange } from "@/components/DateRange/DateRange";
import { formatDate } from "@/components/FormattedDate/FormattedDate";
import { ChevronLeftIcon } from "@/components/Icons/ChevronLeftIcon";
import { ChevronRightIcon } from "@/components/Icons/ChevronRightIcon";
import { buttonVariants } from "@/design-system/Button";
import { FullSizeLoader } from "@/design-system/FullSizeLoader";
import { Inline } from "@/design-system/Inline";
import { dayOfWeekIndex } from "@/helpers/date";
import { twResolve } from "@/helpers/tw-resolve";
import { useInterval } from "@/hooks/useInterval";
import { useTranslation } from "@/translations";
import { RuntimeGridArea, RuntimeGridRows } from "@/ui-adapters/RuntimeGrid";

import { daysOptions } from "../constants";
import { getAllowedViewableDateRange, getDateFromTimeString, isTimeEndOfDay } from "../helpers";
import type { AssetDetailContentProps } from "../pages/asset-detail/AssetDetailContent";
import { ToggleCalendarButton } from "./ToggleCalendarButton";

interface CalendarWeekViewProps {
  assetDetails: AssetDetailContentProps["assetDetails"];
  startOfWeek: AssetDetailContentProps["startDate"];
  endOfWeek: AssetDetailContentProps["endDate"];
  isLoadingBookings: boolean;
  bookings: AssetDetailContentProps["bookings"];
  onChangeWeek: (startDate: Date) => void;
  onChangeView: () => void;
  onSelectBookings: (bookings: AssetBookingDto[]) => void;
}

export function CalendarWeekView({
  assetDetails,
  startOfWeek,
  endOfWeek,
  isLoadingBookings,
  bookings,
  onChangeWeek,
  onChangeView,
  onSelectBookings,
}: CalendarWeekViewProps): React.ReactNode {
  const [today, setToday] = useState(() => new Date());
  const { i18n, t } = useTranslation();
  const container = useRef<HTMLDivElement>(null);
  const containerNav = useRef(null);

  // Update the current time every 5 minutes
  useInterval(
    () => {
      setToday(new Date());
    },
    5 * 60 * 1000,
  );

  useEffect(() => {
    // Scroll past the blocked start hours
    const el = container.current;
    if (el) {
      const minStartHourThisWeek =
        min(
          assetDetails.bookableDays.map((bookableDay) =>
            bookableDay.times && bookableDay.times.length > 0 && bookableDay.times[0].startTime
              ? getDateFromTimeString(bookableDay.times[0].startTime).getHours()
              : 0,
          ),
        ) || 0;

      const minStartHour = Math.max(minStartHourThisWeek, 8);

      const scroll = (minStartHour / 24) * el.scrollHeight - el.offsetHeight * 0.25;

      el.scrollTop = scroll;
    }
  }, [assetDetails.bookableDays, today]);

  const hours = Array(24)
    .fill(0)
    .map((_, i) => i);
  const week = Array(7)
    .fill(0)
    .map((_, i) => {
      const date = addDays(startOfWeek, i);
      const isToday = isSameDay(date, today);
      const weekDay = formatDate(i18n, "weekDay", date);

      return { date, dayNumber: date.getDate(), name: weekDay, isToday };
    });

  const { minViewDate, maxViewDate } = getAllowedViewableDateRange(assetDetails);
  const timeslotUnitSize = 5; // 5 minutes
  const timeslotBlockSize = 30; // 30 minutes
  const amountTotalMinutesInDay = 1440; // 1440 minutes
  const sortedBookableDays = sortBy(assetDetails.bookableDays, (x) => daysOptions.indexOf(x.day));

  const partitionedBookings: AssetBookingDto[][] = useMemo(() => {
    return Object.values(
      bookings.reduce(
        (acc, booking) => {
          const key = `${booking.date}-${booking.startTime}-${booking.endTime}`;
          if (!acc[key]) {
            acc[key] = [];
          }
          acc[key].push(booking);

          return acc;
        },
        {} as Record<string, AssetBookingDto[]>,
      ),
    );
  }, [bookings]);

  return (
    <section className="flex h-full flex-col gap-space-md">
      <header className="flex w-full flex-col-reverse flex-wrap items-center justify-center gap-space-md @md:my-0 @md:flex-row @md:justify-between">
        <Inline gap="sm" align="center" wrap={false}>
          <IconButton
            title={t("page.bookings.asset-detail.week-view.previous")}
            onClick={() => onChangeWeek(addDays(startOfWeek, -7))}
            disabled={startOfWeek < minViewDate}
            size="sm"
            icon={<ChevronLeftIcon />}
          />
          <span className="min-w-select-responsive-filter text-center">
            <DateRange start={startOfWeek} end={addSeconds(endOfWeek, -1)} format="noTime" />
          </span>
          <IconButton
            title={t("page.bookings.asset-detail.week-view.next")}
            onClick={() => onChangeWeek(addDays(startOfWeek, 7))}
            disabled={endOfWeek >= maxViewDate}
            size="sm"
            icon={<ChevronRightIcon />}
          />
        </Inline>
        <ToggleCalendarButton isWeek onToggleView={() => onChangeView()} />
      </header>
      <div
        ref={container}
        className="relative flex max-h-screen-minus-50 flex-auto flex-col overflow-auto bg-background-brand-secondary/5"
      >
        {isLoadingBookings && (
          <div className="absolute inset-0">
            <FullSizeLoader />
          </div>
        )}
        <section className="flex w-full min-w-content-constrained flex-none flex-col @md:min-w-0">
          {/* Weekday labels */}
          <div
            ref={containerNav}
            className="sticky top-0 z-sticky flex-none bg-background-default pr-space-xl ring-1 ring-border-default"
          >
            <header className="-mr-px grid grid-cols-7 divide-x divide-border-default/50 border-r border-border-default/50 text-caption text-text-secondary">
              <div className="col-end-1 w-control-xl" />
              {week.map((day) => (
                <span key={day.dayNumber} className="flex items-baseline justify-center py-space-sm">
                  <span className="capitalize">{day.name.substring(0, 1)}</span>
                  <span className="sr-only @sm:not-sr-only">{day.name.substring(1, 3)}</span>
                  <span className="sr-only @md:not-sr-only">{day.name.substring(3)}</span>
                  <span
                    className={twJoin(
                      "ml-space-xs flex size-icon-prominent items-center justify-center rounded-full text-caption-emphasis @sm:size-icon-display",
                      day.isToday ? "bg-background-brand text-text-default-inverse" : "text-text-primary",
                    )}
                  >
                    {day.dayNumber}
                  </span>
                </span>
              ))}
            </header>
          </div>
          {/* Calendar body */}
          <section className="flex flex-1">
            <RuntimeGridRows template={`repeat(${amountTotalMinutesInDay / timeslotBlockSize}, minmax(2.5rem, 1fr))`}>
              {(runtimeGridAttributes) => (
                <div
                  {...runtimeGridAttributes}
                  className="sticky left-0 z-raised grid w-control-xl shrink-0 bg-background-default ring-1 ring-border-default"
                >
                  {/* Reserved space above 00:00 */}
                  <div className="row-end-1 h-icon-prominent" />
                  {hours.map((hour) => (
                    <Fragment key={hour}>
                      <div className="sticky z-raised h-fit w-control-xl -translate-y-1/2 pr-space-sm text-right text-overline leading-none text-text-secondary">
                        {hour.toString().padStart(2, "0")}:00
                      </div>
                      <div />
                    </Fragment>
                  ))}
                </div>
              )}
            </RuntimeGridRows>
            <div className="grid flex-auto grid-cols-1 grid-rows-1">
              {/* Horizontal lines */}
              <RuntimeGridRows template={`repeat(${amountTotalMinutesInDay / timeslotBlockSize}, minmax(2.5rem, 1fr))`}>
                {(runtimeGridAttributes) => (
                  <div
                    {...runtimeGridAttributes}
                    className="z-content col-start-1 col-end-2 row-start-1 grid divide-y divide-border-secondary/30"
                  >
                    {/* Reserved space above 00:00 */}
                    <div className="row-end-1 h-icon-prominent" />
                    {hours.map((hour) => (
                      <Fragment key={hour}>
                        <div />
                        <div />
                      </Fragment>
                    ))}
                  </div>
                )}
              </RuntimeGridRows>
              {/* Vertical lines */}
              <div className="z-content col-start-1 col-end-2 row-start-1 grid grid-cols-7 grid-rows-1 divide-x divide-border-secondary/30">
                <div className="col-start-1 row-span-full" />
                <div className="col-start-2 row-span-full" />
                <div className="col-start-3 row-span-full" />
                <div className="col-start-4 row-span-full" />
                <div className="col-start-5 row-span-full" />
                <div className="col-start-6 row-span-full" />
                <div className="col-start-7 row-span-full" />
                {/* Reserved space at end of week */}
                <div className="col-start-8 row-span-full w-icon-display" />
              </div>
              {/* Timeslots */}
              <RuntimeGridRows template={`repeat(${amountTotalMinutesInDay / timeslotUnitSize}, minmax(0, 1fr)) auto`}>
                {(runtimeGridAttributes) => (
                  <ol
                    {...runtimeGridAttributes}
                    className="col-start-1 col-end-2 row-start-1 grid grid-cols-7 pt-space-lg pr-space-xl"
                  >
                    {/* Indicator of current time */}
                    <RuntimeGridArea
                      area={`${Math.round(differenceInMinutes(today, startOfDay(today)) / timeslotUnitSize) + 1} / 1 / span 1 / span ${week.length}`}
                    >
                      {(runtimeGridAttributes) => (
                        <li
                          {...runtimeGridAttributes}
                          className="relative z-content col-start-1 col-end-2 border-t-2 border-t-border-brand-tertiary"
                        />
                      )}
                    </RuntimeGridArea>

                    {/* Bookable times */}
                    {sortedBookableDays.map((currBookabbleDay, i) => {
                      const currBookableTimes = currBookabbleDay.times;
                      const publishAt = assetDetails.publishAt ? new Date(assetDetails.publishAt) : undefined;
                      const availableFrom = assetDetails.availableFrom
                        ? new Date(assetDetails.availableFrom)
                        : undefined;
                      const unpublishAt = assetDetails.unpublishAt ? new Date(assetDetails.unpublishAt) : undefined;
                      const currDate = week[i].date;

                      const isNotPublished = publishAt && publishAt > currDate;
                      const isNotAvailable = availableFrom && availableFrom > currDate;
                      const isUnpublished = unpublishAt && unpublishAt < currDate;

                      // If bookable day is not available
                      if (!currBookableTimes || currBookableTimes.length === 0 || !currBookabbleDay.enabled) {
                        return null;
                      }

                      // If bookable day is before the publish date day
                      if (isNotPublished && !isSameDay(publishAt, currDate)) {
                        return null;
                      }

                      // If bookable day is before the available date day
                      if (isNotAvailable && !isSameDay(availableFrom, currDate)) {
                        return null;
                      }

                      // If bookable day is after the unpublish date day
                      if (isUnpublished && !isSameDay(unpublishAt, currDate)) {
                        return null;
                      }

                      return currBookableTimes.map((currBookableTime) => {
                        // End time can be 00:00:00 of the next day
                        let endTimeDate = currDate;
                        if (isTimeEndOfDay(currBookableTime.endTime)) {
                          endTimeDate = addDays(currDate, 1);
                        }

                        const startTime = getDateFromTimeString(currBookableTime.startTime, currDate);
                        const endTime = getDateFromTimeString(currBookableTime.endTime, endTimeDate);
                        // Always use `currDate` to calculate availability of that day
                        let startMinutes = differenceInMinutes(startTime, startOfDay(currDate));
                        let endMinutes = differenceInMinutes(endTime, startOfDay(currDate));

                        // If the bookable time contains publish time
                        if (publishAt && isSameDay(publishAt, currDate)) {
                          startMinutes = Math.max(differenceInMinutes(publishAt, startOfDay(publishAt)), startMinutes);

                          if (startMinutes >= endMinutes) {
                            return null;
                          }
                        }

                        // If the bookable time contains available time
                        if (availableFrom && isSameDay(availableFrom, currDate)) {
                          startMinutes = Math.max(
                            differenceInMinutes(availableFrom, startOfDay(availableFrom)),
                            startMinutes,
                          );

                          if (startMinutes >= endMinutes) {
                            return null;
                          }
                        }

                        // If the bookable time contains unpublish time
                        if (unpublishAt && isSameDay(unpublishAt, currDate)) {
                          endMinutes = Math.min(differenceInMinutes(unpublishAt, startOfDay(unpublishAt)), endMinutes);

                          if (startMinutes >= endMinutes) {
                            return null;
                          }
                        }

                        return (
                          <RuntimeGridArea
                            key={`timeslot-${i}-${currBookableTime.startTime}-${currBookableTime.endTime}`}
                            area={`${Math.round(startMinutes / timeslotUnitSize) + 1} / ${i + 1} / ${endMinutes / timeslotUnitSize + 1}`}
                          >
                            {(runtimeGridAttributes) => (
                              <li {...runtimeGridAttributes} className="bg-background-default" />
                            )}
                          </RuntimeGridArea>
                        );
                      });
                    })}

                    {/* Bookings */}
                    {partitionedBookings.map((currBookings) => {
                      // Pick any one from the bookings to read the relevant information
                      const sampleBooking = currBookings[0];
                      const date = parse(sampleBooking.date, "yyyy-MM-dd", startOfDay(today));
                      const startDate = parse(sampleBooking.startTime, "HH:mm:ss", date);
                      let endDate = parse(sampleBooking.endTime, "HH:mm:ss", date);
                      if (isTimeEndOfDay(sampleBooking.endTime)) {
                        endDate = addSeconds(addDays(endDate, 1), -1);
                      }
                      const daysSinceStartWeek = differenceInDays(date, startOfWeek);
                      const durationInHours = (endDate.getTime() - startDate.getTime()) / 1000 / 60 / 60;
                      const inPast = endDate < today;
                      const startMinutes = Math.round(
                        differenceInMinutes(startDate, startOfDay(startDate)) / timeslotUnitSize,
                      );
                      const endMinutes = Math.round(
                        differenceInMinutes(endDate, startOfDay(endDate)) / timeslotUnitSize,
                      );

                      const startTimeLabel =
                        startDate.getHours() + ":" + startDate.getMinutes().toString().padStart(2, "0");
                      const authorLabel =
                        sampleBooking.author?.fullName ??
                        (sampleBooking.bookingReason === "External JIM booking"
                          ? sampleBooking.bookingReason
                          : t("page.bookings.asset-detail.author-fallback"));

                      return (
                        <RuntimeGridArea
                          key={sampleBooking.id}
                          area={`${startMinutes + 1} / ${daysSinceStartWeek + 1} / ${endMinutes + 1}`}
                        >
                          {(runtimeGridAttributes) => (
                            <li
                              {...runtimeGridAttributes}
                              className={twJoin(
                                "relative z-raised flex items-center justify-center p-space-xs",
                                getSmColStart(startDate),
                              )}
                            >
                              <button
                                data-testid="booked-slot-time-btn"
                                type="button"
                                onClick={() => onSelectBookings(currBookings)}
                                className={twResolve(
                                  buttonVariants({ variant: "subtle", size: "sm", fullWidth: true }),
                                  "group top-0 flex h-full rounded-lg transition-colors",
                                  inPast
                                    ? "bg-background-brand-secondary/80 hover:bg-background-brand-secondary/70"
                                    : "bg-background-brand-secondary hover:bg-background-brand-secondary/80",
                                  durationInHours <= 0.5 ? "gap-space-xs" : "flex-col",
                                  durationInHours <= 0.25
                                    ? "inset-y-space-xs overflow-y-visible px-space-sm py-space-xs leading-none"
                                    : "inset-y-space-xs overflow-y-auto p-space-sm leading-none",
                                )}
                              >
                                <p className="text-left text-overline text-text-default-inverse">
                                  <time dateTime={startDate.toISOString()}>{startTimeLabel}</time>
                                </p>
                                <p className="text-left text-overline-emphasis text-text-default-inverse">
                                  <span className={durationInHours <= 1 ? "line-clamp-1" : undefined}>
                                    {authorLabel}
                                  </span>
                                </p>
                                {currBookings.length > 1 && (
                                  <p
                                    className={twJoin(
                                      "truncate text-left text-overline text-text-default-inverse",
                                      durationInHours <= 0.5 ? "ml-auto" : "mt-auto",
                                    )}
                                  >
                                    {t("page.bookings.asset-detail.more-authors", {
                                      count: currBookings.length - 1,
                                    })}
                                  </p>
                                )}
                              </button>
                            </li>
                          )}
                        </RuntimeGridArea>
                      );
                    })}
                  </ol>
                )}
              </RuntimeGridRows>
            </div>
          </section>
        </section>
      </div>
    </section>
  );
}

// No string interpolation because tailwind would not find the classes
function getSmColStart(day: Date) {
  switch (dayOfWeekIndex(day)) {
    case 0:
      return "@sm:col-start-1";
    case 1:
      return "@sm:col-start-2";
    case 2:
      return "@sm:col-start-3";
    case 3:
      return "@sm:col-start-4";
    case 4:
      return "@sm:col-start-5";
    case 5:
      return "@sm:col-start-6";
    case 6:
      return "@sm:col-start-7";
    default:
      throw new Error(`Day of week index is incorrect`);
  }
}
